import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaHeartbeat, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaVideo,
  FaChartLine,
  FaMicroscope,
  FaStethoscope,
  FaProcedures,
  FaCalendarAlt
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
  
  svg {
    font-size: 2.5rem;
    color: #ffd966;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #0a2b3c;
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
  }
  
  p {
    color: #5a6b7a;
    line-height: 1.6;
    font-size: 0.95rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ModuleCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  h4 {
    color: #0a4d68;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    border-bottom: 1px solid rgba(255, 217, 102, 0.3);
    padding-bottom: 0.5rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const TestimonialSlider = styled.div`
  background: #f8fafc;
  border-radius: 20px;
  padding: 2rem;
  margin: 3rem 0;
  border-left: 4px solid #ffd966;
  
  p {
    font-size: 1.1rem;
    font-style: italic;
    color: #2c3e50;
    margin-bottom: 1rem;
  }
  
  .author {
    color: #0a4d68;
    font-weight: 600;
  }
  
  .role {
    color: #5a6b7a;
    font-size: 0.9rem;
  }
`;

const CardiovascularCoding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  

  const features = [
    {
      icon: <FaHeartbeat />,
      title: "Cardiac Anatomy Mastery",
      description: "In-depth understanding of cardiac anatomy and its relevance to coding cardiovascular procedures."
    },
    {
      icon: <FaMicroscope />,
      title: "Procedure Understanding",
      description: "Detailed knowledge of cardiac catheterization, pacemaker insertions, and congenital heart defect repairs."
    },
    {
      icon: <FaProcedures />,
      title: "Bifurcation Expertise",
      description: "Learn to identify and assign correct qualifier codes for complex coronary artery bifurcation procedures."
    }
  ];

  const modules = [
    {
      title: "Cardiac Catheterization & Interventions",
      topics: [
        "Diagnostic cardiac catheterization (93451-93598)",
        "Coronary angiography and ventriculography",
        "Percutaneous coronary interventions (PCI)",
        "Atherectomy and thrombectomy procedures"
      ]
    },
    {
      title: "Electrophysiology & Pacemakers",
      topics: [
        "Electrophysiological studies (EPS)",
        "Ablation procedures for arrhythmias",
        "Pacemaker and ICD insertions",
        "Lead placement and generator changes"
      ]
    },
    {
      title: "Congenital Heart Defect Repairs",
      topics: [
        "Norwood procedure",
        "Bidirectional Glenn shunt",
        "Fontan procedure",
        "Septal defect repairs"
      ]
    },
    {
      title: "Cardiac Surgeries & Valve Procedures",
      topics: [
        "CABG (Coronary Artery Bypass Grafting)",
        "Heart valve repairs and replacements",
        "Endovascular aneurysm repair (EVAR)",
        "Impella device insertion and removal"
      ]
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Cardiovascular Coding
          <span>Master ICD-10-PCS Cardiovascular Procedures</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive training in cardiovascular procedure coding. Learn to accurately code cardiac 
          catheterizations, pacemakers, congenital heart defect repairs, and more. [citation:1]
        </HeroDescription>
      </HeroSection>

      <Container>
        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              This comprehensive Training Plan offers a deep dive into the intricate world of cardiovascular 
              procedures in ICD-10-PCS coding. It equips learners with the expertise needed to accurately 
              assign codes for a wide range of cardiac interventions, from cardiac catheterization to 
              congenital heart defect repairs, pacemaker procedures, and more. [citation:1]
            </p>
            <p>
              Through detailed explanations, visual aids like photos and videos, and discussions on relevant 
              Official Coding Guidelines (OCG), participants gain a comprehensive understanding of the 
              anatomy, clinical aspects, and technical components of these procedures, ensuring precise 
              code assignment. [citation:1]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Cardiovascular procedure"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>Key Learning Outcomes</SectionTitle>
        <Grid3Col>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              {feature.icon}
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </FeatureCard>
          ))}
        </Grid3Col>

        <SectionTitle>Course Curriculum</SectionTitle>
        <ModuleGrid>
          {modules.map((module, index) => (
            <ModuleCard key={index}>
              <h4>{module.title}</h4>
              <List>
                {module.topics.map((topic, idx) => (
                  <ListItem key={idx}>
                    <FaCheckCircle /> {topic}
                  </ListItem>
                ))}
              </List>
            </ModuleCard>
          ))}
        </ModuleGrid>

        <TestimonialSlider>
          <p>
            "This cardiovascular coding course transformed my understanding of complex cardiac procedures. 
            The detailed explanations of bifurcation coding and congenital heart defect repairs gave me 
            confidence I never had before."
          </p>
          <div className="author">— Dr. Sarah Mitchell</div>
          <div className="role">Cardiovascular Coder, Certified</div>
        </TestimonialSlider>

        <CTASection>
          <h3>Ready to Master Cardiovascular Coding?</h3>
          <p>Join our next batch and become an expert in cardiac procedure coding</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default CardiovascularCoding;